import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  IS_MOCK,
  getMockRole,
  isMockSignedOut,
  setMockRole,
  setMockSignedOut,
} from '@/mocks/config'
import { ROLE_LABEL, type Role } from '@/features/auth/types'
import { cn } from '@/lib/utils'

const ROLES: Role[] = ['super', 'ops']

/**
 * Dev-only chrome, ported from the prototype's demo bar. Lets you flip between
 * the two staff roles and sign out without a backend, so the RBAC-dependent UI
 * is reviewable before RBAC exists.
 *
 * Renders nothing at all unless mock mode is active.
 */
export function MockBar() {
  const queryClient = useQueryClient()
  const [role, setRole] = useState<Role>(() => (IS_MOCK ? getMockRole() : 'super'))
  const [signedOut, setSignedOut] = useState(() => IS_MOCK && isMockSignedOut())

  if (!IS_MOCK) return null

  function applyRole(next: Role) {
    setMockRole(next)
    setRole(next)
    // Session refetches, nav re-renders, and RequireRole bounces you off any
    // super-only route — same behaviour as the prototype's role toggle.
    void queryClient.invalidateQueries()
  }

  function applySignedOut(next: boolean) {
    setMockSignedOut(next)
    setSignedOut(next)
    void queryClient.invalidateQueries()
  }

  return (
    <div className="sticky top-0 z-50 flex flex-wrap items-center gap-3.5 border-b border-[#f3dfa8] bg-[#fff7e6] px-4 py-2 text-[12.5px] text-[#7a5b00]">
      <b>Mock mode</b>
      <span className="opacity-80">No backend — fixture data.</span>

      <div className="flex overflow-hidden rounded-full border border-[#eadfb8]">
        {ROLES.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => applyRole(option)}
            className={cn(
              'px-3 py-1 text-xs',
              role === option && !signedOut
                ? 'bg-[#7a5b00] text-[#fff7e6]'
                : 'bg-[#fffdf7] text-[#7a5b00]',
            )}
          >
            {ROLE_LABEL[option]}
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={() => applySignedOut(!signedOut)}
        className="rounded-full border border-[#eadfb8] bg-[#fffdf7] px-3 py-1 text-xs"
      >
        {signedOut ? 'Sign back in' : 'Sign out'}
      </button>

      <span className="ml-auto text-[11.5px] opacity-80">
        Set <code>VITE_MOCK_API=false</code> to hit the real backend.
      </span>
    </div>
  )
}
